"use client";

import { Hammer, Lightbulb, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function AdvancedDataAnalytics() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const features = [{
    icon: Hammer,
    title: "Data Analytics",
    description: "Transform complex information into clear reports, interactive dashboards, and meaningful insights that support faster and more confident decision-making."
  }, {
    icon: Lightbulb,
    title: "Data Automation & Technology",
    description: "Automate repetitive tasks, connect systems, and create tailored technology solutions that reduce manual work, improve accuracy, and strengthen operational efficiency."
  }, {
    icon: Sparkles,
    title: "Tailored Business Support",
    description: "Every engagement is shaped around your organisation’s requirements, priorities, and way of working, providing flexible support where it delivers the greatest value."
  }];
  return <section data-section-id="1550" id="advanced-data-analytics" className="text-foreground relative overflow-hidden py-24 md:py-32 dark bg-accent">
      {}
      

      {}
      

      {}
      <svg aria-hidden="true" className="hidden md:block absolute bottom-32 left-10 w-12 h-12 text-secondary opacity-70" viewBox="0 0 50 50">
        <path d="M25,5 L29,20 L44,22 L32,32 L36,46 L25,38 L14,46 L18,32 L6,22 L21,20 Z" fill="none" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" />
      </svg>

      <div className="relative max-w-[1360px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftRef} className="lg:col-span-5 lg:sticky lg:top-24 self-start relative" style={{
          opacity: leftVisible ? 1 : 0,
          transform: leftVisible ? "translateY(0) rotate(0deg)" : "translateY(28px) rotate(0deg)",
          transition: "all 600ms cubic-bezier(0.16, 1, 0.3, 1)"
        }}>
            {}
            <div aria-hidden="true" className="absolute -top-10 -left-4 font-default text-[14rem] md:text-[18rem] leading-none pointer-events-none select-none" style={{
            WebkitTextStroke: "2px hsl(217 91% 60% / 0.1)",
            color: "transparent"
          }}>
              01
            </div>

            <div className="relative">
              {}
              <div className="inline-flex items-center gap-2 bg-secondary text-secondary-foreground border border-border px-4 py-1.5 rounded-lg font-default text-xs tracking-[0.2em] uppercase font-medium" style={{
              boxShadow: "var(--shadow-md)"
            }}>
                Infinix / Our Services
              </div>

              {}
              <h2 className="font-default font-semibold mt-8 text-foreground" style={{
              fontSize: "clamp(2.5rem, 5vw, 5rem)",
              letterSpacing: "-0.025em",
              lineHeight: 0.94
            }}>
                Practical solutions.
                <br />
                Measurable{" "}
                <span className="relative inline-block">
                  <svg aria-hidden="true" className="absolute -inset-3 w-[calc(100%+1.5rem)] h-[calc(100%+1.5rem)] -z-0" viewBox="0 0 160 100" preserveAspectRatio="none">
                    <ellipse cx="80" cy="50" rx="72" ry="38" fill="none" stroke="hsl(199 89% 48%)" strokeWidth="2" strokeLinecap="round" style={{
                    transform: "rotate(-2deg)",
                    transformOrigin: "center"
                  }} />
                  </svg>

                  <span className="relative z-10">impact</span>
                </span>
                .
              </h2>

              {}
              <p className="mt-8 text-muted-foreground max-w-xl" style={{
              fontSize: "clamp(1.125rem, 1.3vw, 1.375rem)",
              lineHeight: 1.55
            }}>
                We help businesses turn complex data, repetitive processes,
                and financial demands into clear, efficient, and reliable ways
                of working. Explore our specialist service areas, each
                tailored to your organisation’s needs.
              </p>

              {}
              <div className="mt-10">
                <Link to="/OurServicesPage">
                  
                </Link>
              </div>
            </div>
          </div>

          {}
          <div ref={rightRef} className="lg:col-span-7 flex flex-col">
            {features.map((feature, i) => {
            const Icon = feature.icon;
            const isLast = i === features.length - 1;
            const serviceSlug = feature.title.toLowerCase().replace(/ & /g, "-").replace(/\s+/g, "-");
            return <div key={feature.title} data-index={i} className="group relative" style={{
              opacity: rightVisible ? 1 : 0,
              transform: rightVisible ? "translateY(0) rotate(0deg)" : "translateY(28px) rotate(0deg)",
              transition: `all 600ms cubic-bezier(0.16, 1, 0.3, 1) ${i * 80}ms`
            }}>
                  <div className="grid grid-cols-[auto_1fr] gap-5 md:gap-7 py-8 md:py-10">
                    {}
                    <div className="relative">
                      <div className="w-16 h-16 rounded-lg border border-border bg-card flex items-center justify-center text-secondary transition-colors group-hover:border-secondary/50" style={{
                    boxShadow: "var(--shadow-md)"
                  }}>
                        <Link to={i === 0 ? "/DataAnalyticsPage" : `/services/${serviceSlug}`} className="flex items-center justify-center w-full h-full">
                          <Icon className="w-8 h-8" strokeWidth={1.5} aria-hidden="true" />
                        </Link>
                      </div>
                    </div>

                    {}
                    <div>
                      <h3 className="font-default font-semibold text-foreground inline-block relative" style={{
                    fontSize: "clamp(1.5rem, 2.4vw, 2.25rem)",
                    letterSpacing: "-0.015em",
                    lineHeight: 1
                  }}>
                        {feature.title}
                      </h3>

                      <p className="mt-4 text-muted-foreground" style={{
                    fontSize: "1.0625rem",
                    lineHeight: 1.6
                  }}>
                        {feature.description}
                      </p>
                    </div>
                  </div>

                  {}
                  {!isLast && <div className="w-full h-px bg-border" />}
                </div>;
          })}
          </div>
        </div>
      </div>

      {}
      
    </section>;
}